// apps/web/src/screens/home/home.screen.tsx
//
// Post-sign-in `/app` home. Two constraints the scaffold must NOT relax:
//   1. The h1 text MUST start with the literal "You logged in successfully
//      as <username>." -- the e2e smoke spec asserts on it.
//   2. The screen MUST NOT call any /api/* endpoint on mount.

import type { JSX } from 'react';

import { Button } from '@/components/ui/button.tsx';
import { authService } from '@/services/auth.service.ts';

export function HomeScreen(): JSX.Element {
  const session = authService.getSession();
  const username =
    session?.idTokenClaims.email ??
    (session?.idTokenClaims['cognito:username'] as string | undefined) ??
    'unknown user';

  return (
    <main
      aria-labelledby="home-heading"
      className="mx-auto flex min-h-svh max-w-2xl flex-col justify-center gap-6 px-4 py-10"
    >
      <header className="flex flex-col gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Breeze
        </span>
        <h1 className="text-2xl font-semibold" id="home-heading">
          You logged in successfully as {username}.
        </h1>
        <p className="text-base text-slate-700">
          This is the placeholder home for Breeze. The scaffold seeded it so the sign-in
          round-trip is verifiable end-to-end before any feature work begins. Add real
          screens under <code className="mx-1 rounded bg-slate-100 px-1 py-0.5 text-sm">/app/&lt;feature&gt;</code>
          and they will inherit the same auth wrapper automatically.
        </p>
      </header>

      <section className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          What works today
        </h2>
        <ul className="list-disc pl-5 text-sm text-slate-700">
          <li>Sign in with the test user (dev) or your Google account (prod).</li>
          <li>Runtime config is loaded from the env-specific <code>env.json</code>.</li>
          <li>The API health endpoint is reachable cross-origin with CORS.</li>
        </ul>
      </section>

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => authService.signOut()} type="button" variant="secondary">
          Sign out
        </Button>
      </div>
    </main>
  );
}
